import React from 'react'
import {useTranslation} from "react-i18next";
import {useFormik} from "formik";
import * as yup from "yup";

import {useCreateUser} from "./query";
import WorkgroupsAutocomplete from "../../../components/inputs/WorkgroupsAutocomplete";
import GroupsAutocomplete from "../../../components/inputs/GroupAutocomplete";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Icon from "@mui/material/Icon";
import MDInput from "../../../materialDashboard/components/MDInput";

import MDBox from "../../../materialDashboard/components/MDBox";
import MDTypography from "../../../materialDashboard/components/MDTypography";
import MDButton from "../../../materialDashboard/components/MDButton";
import {useCurrentUser} from "../../../auth/CurrentUserProvider";
import LocaleAutocomplete from "../../../components/inputs/LocaleAutocomplate";


const UserCreate = () => {
    const {t} = useTranslation(['users', 'validation', 'enums']);
    const {currentUser} = useCurrentUser();
    const role = currentUser?.data?.role ?? null;
    const allowedRoles = role === "ADMIN" ? ["ADMIN", "WG_ADMIN", "WG_USER"] : ["WG_USER"];

    const userMutation = useCreateUser();

    const formik = useFormik({
        initialValues: {
            role: "WG_USER",
            name: "",
            surname: "",
            email: "",
            password: "",
            passwordConfirmation: "",
            defaultLocale: null,
            workgroups: [],
            groups: [],
            isActive: true,
        },
        validationSchema: yup.object({
            role: yup.string().oneOf(allowedRoles).required(t('validation:required')),
            name: yup
                .string()
                .required(t('validation:required')),
            surname: yup
                .string()
                .required(t('validation:required')),
            email: yup
                .string()
                .email(t('validation:email'))
                .required(t('validation:required')),
            password: yup.string()
                .min(8, t('validation:minLength', {min: 8}))
                .required(t('validation:required')),
            passwordConfirmation: yup.string()
                .oneOf([yup.ref('password'), null], t('validation:passwordsMustMatch'))
                .required(t('validation:required')),
            workgroups: yup
                .array()
                .required(t('validation:required')),
            groups: yup
                .array()
                .required(t('validation:required')),
            isActive: yup
                .boolean()
                .required(t('validation:required')),
        }),
        onSubmit: async (values) => {
            const {workgroups, groups, passwordConfirmation, ...rest} = values;
            const workgroupIds = workgroups.map(({id}) => id);
            const groupsIds = groups.map(({id}) => id)
            await userMutation.mutateAsync({...rest, workgroupIds: workgroupIds, groupIds: groupsIds});
        }
    });

    const handleChangeRole = (role) => () => {
        formik.setFieldValue("role", role);
    }

    const workgroupIds = formik.values.workgroups.map(w => w.id);

    return <Grid container justifyContent="center" alignItems="center" mt={4}>
        <Grid item xs={12} lg={8}>
            <Card>
                <MDBox
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                    mx={2}
                    mt={-3}
                    p={2}
                    mb={1}
                    textAlign="center"
                >
                    <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
                        {t('userCreate.title')}
                    </MDTypography>
                </MDBox>

                <Stack direction="column" spacing={3} p={3}>
                    <Stack spacing={1} direction="row" justifyContent="space-evenly">
                        {allowedRoles.includes("WG_USER") && <MDButton variant={formik.values.role === "WG_USER" ? "contained" : "outlined"}
                                   color="info"
                                   sx={{width: "8.25rem", height: "6.125rem"}}
                                   disableRipple
                                   onClick={handleChangeRole("WG_USER")}>
                            <Stack direction="column" alignItems="center">
                                <Icon>person</Icon>
                                {t('enums:role.WG_USER')}
                            </Stack>
                        </MDButton>}
                        {allowedRoles.includes("WG_ADMIN") && <MDButton variant={formik.values.role === "WG_ADMIN" ? "contained" : "outlined"}
                                   color="info"
                                   sx={{width: "8.25rem", height: "6.125rem"}}
                                   disableRipple
                                   onClick={handleChangeRole("WG_ADMIN")}>
                            <Stack direction="column" alignItems="center">
                                <Icon>person</Icon>
                                {t('enums:role.WG_ADMIN')}
                            </Stack>
                        </MDButton>}
                        {allowedRoles.includes("ADMIN") && <MDButton variant={formik.values.role === "ADMIN" ? "contained" : "outlined"}
                                   color="info"
                                   sx={{width: "8.25rem", height: "6.125rem"}}
                                   disableRipple
                                   onClick={handleChangeRole("ADMIN")}>
                            <Stack direction="column" alignItems="center">
                                <Icon>person</Icon>
                                {t('enums:role.ADMIN')}
                            </Stack>
                        </MDButton>}
                    </Stack>

                    <MDBox>
                        <MDBox mb={2}>
                            <MDTypography variant="h5">
                                {t('userCreate.accountInfo')}
                            </MDTypography>
                        </MDBox>
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={12} lg={6}>
                                <MDInput name="name"
                                         label={t('userCreate.name')}
                                         value={formik.values.name}
                                         onChange={formik.handleChange}
                                         error={formik.touched.name && Boolean(formik.errors.name)}
                                         helperText={formik.touched.name && formik.errors.name}
                                         variant="standard"
                                         fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <MDInput name="surname"
                                         label={t('userCreate.surname')}
                                         value={formik.values.surname}
                                         onChange={formik.handleChange}
                                         error={formik.touched.name && Boolean(formik.errors.surname)}
                                         helperText={formik.touched.name && formik.errors.surname}
                                         variant="standard"
                                         fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <MDInput name="email"
                                         label={t('userCreate.email')}
                                         value={formik.values.email}
                                         onChange={formik.handleChange}
                                         error={formik.touched.email && Boolean(formik.errors.email)}
                                         helperText={formik.touched.email && formik.errors.email}
                                         variant="standard"
                                         fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <LocaleAutocomplete name="defaultLocale"
                                                    fullWidth
                                                    value={formik.values.defaultLocale}
                                                    onChange={formik.handleChange}
                                                    error={formik.touched.defaultLocale && Boolean(formik.errors.defaultLocale)}
                                                    helperText={formik.touched.defaultLocale && formik.errors.defaultLocale}/>
                            </Grid>
                        </Grid>
                    </MDBox>

                    <MDBox>
                        <MDBox mb={2}>
                            <MDTypography variant="h5">
                                {t('userCreate.passwordInfo')}
                            </MDTypography>
                        </MDBox>
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={12} lg={6}>
                                <MDInput name="password"
                                         label={t('userCreate.password')}
                                         value={formik.values.password}
                                         onChange={formik.handleChange}
                                         error={formik.touched.password && Boolean(formik.errors.password)}
                                         helperText={formik.touched.password && formik.errors.password}
                                         variant="standard"
                                         type="password"
                                         fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <MDInput name="passwordConfirmation"
                                         label={t('userCreate.passwordConfirmation')}
                                         value={formik.values.passwordConfirmation}
                                         onChange={formik.handleChange}
                                         error={formik.touched.passwordConfirmation && Boolean(formik.errors.passwordConfirmation)}
                                         helperText={formik.touched.passwordConfirmation && formik.errors.passwordConfirmation}
                                         variant="standard"
                                         type="password"
                                         fullWidth
                                />
                            </Grid>
                        </Grid>
                    </MDBox>

                    {
                        formik.values.role !== "ADMIN" && <MDBox>
                            <MDBox>
                                <MDBox mb={2}>
                                    <MDTypography variant="h5">
                                        {t('userCreate.workgroupsInfo')}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            <Grid container spacing={2} mb={2}>
                                <Grid item xs={12}>
                                    <WorkgroupsAutocomplete selectedWorkgroups={formik.values.workgroups}
                                                            setSelectedWorkgroups={value => formik.setFieldValue('workgroups', value)}
                                                            workgroupFilters={{
                                                                acl__users_write: true
                                                            }}
                                                            fieldProps={
                                                                {
                                                                    error: formik.touched.workgroups && Boolean(formik.errors.workgroups),
                                                                    helperText: formik.touched.workgroups && formik.errors.workgroups,
                                                                }
                                                            }/>
                                </Grid>
                                { formik.values.role !== "WG_ADMIN" &&
                                    <Grid item xs={12}>
                                        <GroupsAutocomplete workgroupIds={workgroupIds}
                                                            selectedGroups={formik.values.groups}
                                                            setSelectedGroups={value => formik.setFieldValue('groups', value)}
                                                            disabled={workgroupIds.length === 0}
                                                            fieldProps={
                                                                {
                                                                    error: formik.touched.groups && Boolean(formik.errors.groups),
                                                                    helperText: formik.touched.groups && formik.errors.groups,
                                                                }
                                                            }/>
                                    </Grid>
                                }
                            </Grid>
                        </MDBox>
                    }

                    <Stack direction="row" alignItems="center">
                        <MDBox mt={0.5}>
                            <Switch checked={formik.values.isActive} onChange={() => (
                                formik.setFieldValue('isActive', !formik.values.isActive)
                            )}/>
                        </MDBox>
                        <MDBox width="80%" ml={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                                {t('userCreate.enabled')}
                            </MDTypography>
                        </MDBox>
                    </Stack>

                    <Stack justifyContent="flex-end">
                        <MDButton variant="gradient" color="info" onClick={formik.handleSubmit} sx={{ml: "auto"}}>
                            {t('userCreate.submit')}
                        </MDButton>
                    </Stack>

                </Stack>
            </Card>
        </Grid>
    </Grid>
}

export default UserCreate;